// Used in assets.effect.ts, resource-container.component.ts, algolia-filters.component.ts, algolia-search-widget.component.ts,
// student-resources-container.component.ts, content-library-container.component.ts

// Angular
import { Injectable } from "@angular/core";

// Services/Types/Helpers
import { LanguageHelper } from "#helpers/language.helper";
import { PPAssignment as Assignment } from "cli-types";

// Algolia
import { SearchClient, algoliasearch } from "algoliasearch";

import { Asset, Edition, assetTypeIds } from "@carnegie-learning/cure-types";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";

export const searchClient = algoliasearch(
    (<any>window).ALGOLIA_APP_ID || "ALGOLIA_APP_ID",
    (<any>window).ALGOLIA_APP_KEY || "ALGOLIA_APP_KEY"
);

@Injectable()
export class AlgoliaSearchClientHelper {
    algoliaIndexName: string = (<any>window).ALGOLIA_INDEX_NAME;

    constructor(private languageHelper: LanguageHelper) {}

    getClient(): SearchClient {
        return searchClient;
    }

    getIndexName(): string {
        return this.algoliaIndexName;
    }

    createAlgoliaFilter(
        params: FeathersServicePaginationParameters<Asset>,
        resourcesTab: boolean,
        isTeacher: boolean,
        edition?: Edition,
        allCourseAssignments?: Assignment[]
    ): string {
        const filters: string[] = [];
        if (params && params.filter) {
            const updatedFilterObj = this.setFiltersCondition(
                params.filter,
                resourcesTab,
                isTeacher,
                edition,
                allCourseAssignments
            );
            for (const key in updatedFilterObj) {
                if (Array.isArray((<any>updatedFilterObj)[key]) && (<any>updatedFilterObj)[key].length > 0) {
                    const orFilter = (<any>updatedFilterObj)[key].map((v: any) => {
                        // TODO: It certainly looks like v should be a string, but that breaks.
                        if (key === "assetTypeId") {
                            if (v === assetTypeIds.BUNDLE) {
                                return 'assetActivityTypeId: "' + v + '"';
                            } else {
                                const tempKey = Object.values(assetTypeIds).includes(v)
                                    ? "assetTypeId"
                                    : "assetActivityTypeId";
                                return tempKey + ':"' + v + '"';
                            }
                        } else if (key !== "" && v !== "") {
                            return key + ':"' + v + '"';
                        }
                    });
                    const filter = "(" + orFilter.join(" OR ") + ")";
                    filters.push(filter);
                } else if (
                    typeof (<any>updatedFilterObj)[key] === "string" ||
                    typeof (<any>updatedFilterObj)[key] === "number"
                ) {
                    const filter = key + ':"' + (<any>updatedFilterObj)[key] + '"';
                    filters.push(filter);
                } else if (typeof (<any>updatedFilterObj)[key] === "boolean") {
                    const filter = key + ":" + (<any>updatedFilterObj)[key];
                    filters.push(filter);
                }
            }
            filters.push("isDeleted:false");
        }
        return filters
            .filter((f) => f.match(/[a-zA-Z]/)) // filter out if filter doesn't contain any character
            .join(" AND ");
    }

    setFiltersCondition(
        filter: Object,
        resourcesTab: boolean,
        isTeacher: boolean,
        edition?: Edition,
        allCourseAssignments?: Assignment[]
    ): Object {
        // Set audiences and locales
        if (resourcesTab) {
            filter = {
                ...filter,
                audiences: isTeacher ? ["TCH"] : ["STU"],
            };
        } else if (edition && this.languageHelper.isMathProgram(edition.subjectId)) {
            filter = {
                ...filter,
                locales: ["en", "en_US"],
            };
        }
        // Assigned in class filter: adding teacher-class assignments' assetIds to param filter
        if (filter.hasOwnProperty("courseID")) {
            const selectedCourses = <string[]>(<any>filter)["courseID"];
            const assetIds = allCourseAssignments
                ?.map((assignment) => {
                    if (selectedCourses.includes(String(assignment.courseID))) {
                        return assignment.assetId;
                    }
                })
                .filter((a) => a !== undefined);
            if (assetIds && assetIds.length) {
                filter = {
                    ...filter,
                    assetId: assetIds,
                };
                delete (<any>filter)["courseID"];
            }
        } else {
            delete (<any>filter)["assetId"];
        }
        // adding supplementEditionId to editionIds
        if ((<Asset>filter).editionIds && edition?.supplementEditionId) {
            const editionIds = [...(<Asset>filter).editionIds, edition.supplementEditionId];
            filter = {
                ...filter,
                editionIds,
            };
        }
        // visibility condition
        if ((<Asset>filter).visibility) {
            filter = {
                ...filter,
                visibility: this.getVisibilityWithExceptions(filter),
            };
        }

        return filter;
    }

    /* searchFacetParams filter should be generic and not include filter selection. i.e. assetType, units, lessons
    and it should include editionIds, isAssignable and isResource */
    getFilteredParams(
        params?: FeathersServicePaginationParameters<Asset>
    ): FeathersServicePaginationParameters<Asset> | undefined {
        let filteredParams;
        if (params) {
            const { isAssignable, isResource, editionIds, ...rest } = params.filter;
            filteredParams = {
                ...params,
                filter: {
                    isAssignable,
                    isResource,
                    editionIds,
                },
            };
        }

        return filteredParams || params;
    }

    // There are some exceptions for the visibility to ensure showing
    // i.e. userA created "school" asset, we still want to see it when user select filter "self"
    getVisibilityWithExceptions(filter: Object): string[] {
        const selectedVisibility: string[] = (<any>filter).visibility;
        let queryVisibility: string[] = [];

        // if only self is selected, school, district, and none are needed to include in the query. In order to show ALL user created assets
        // We should not be able to view the assets created by other users because the filter params should have the publishedBy for this userGUID.
        if (selectedVisibility.includes("self")) {
            queryVisibility = ["self", "school", "district", "none"];
            if (selectedVisibility.includes("global")) {
                queryVisibility.push("global");
            }
        } else {
            queryVisibility = selectedVisibility;
        }

        return queryVisibility;
    }
}
